/* eslint-disable no-empty */

// Event queue and throttling to prevent ERR_INSUFFICIENT_RESOURCES
const eventQueue = [];
let isProcessingQueue = false;
const EVENT_THROTTLE_MS = 500; // Minimum time between events
const MAX_QUEUE_SIZE = 50; // Maximum events to queue before dropping

/**
 * @returns {boolean} Whether the New Relic agent is enabled.
 */
export const isNewRelicEnabled = () => typeof window !== 'undefined' && window.newrelic;

/**
 * Process the event queue with throttling
 */
const processEventQueue = () => {
  if (isProcessingQueue || eventQueue.length === 0) return;

  isProcessingQueue = true;

  const sendNextEvent = () => {
    if (eventQueue.length === 0) {
      isProcessingQueue = false;
      return;
    }

    const { eventName, eventAttributes } = eventQueue.shift();

    if (isNewRelicEnabled() && window?.newrelic?.addPageAction) {
      try {
        window.newrelic.addPageAction(eventName, eventAttributes);
      } catch {}
    }

    // Schedule next event with throttle delay
    if (eventQueue.length > 0) {
      setTimeout(sendNextEvent, EVENT_THROTTLE_MS);
    } else {
      isProcessingQueue = false;
    }
  };

  sendNextEvent();
};

/**
 * @param {string} eventName Name of the event to send to NewRelic.
 * @param {Object.<string, *> = {}} eventAttributes Any additional attributes to send with the event.
 * @returns {void}
 */
export const sendNewRelicEvent = (eventName, eventAttributes = {}) => {
  // Drop events if queue is too large (prevents memory issues)
  if (eventQueue.length >= MAX_QUEUE_SIZE) {
    return;
  }

  eventQueue.push({ eventAttributes, eventName });
  processEventQueue();
};

/**
 * @param {*} error The error object to send to NewRelic.
 * @param {Object.<string, *> = {}} errorAttributes Any additional attributes to send with the error.
 * @returns {void}
 */
export const sendNewRelicError = (error, errorAttributes = {}) => {
  if (isNewRelicEnabled() && window?.newrelic?.noticeError) {
    try {
      window.newrelic.noticeError(error, errorAttributes);
    } catch {}
  }
};

/**
 *
 * @param {string} name
 * @param {string|number|boolean|null} value
 * @returns {void}
 */
export const setNewRelicAttribute = (name, value = '') => {
  if (isNewRelicEnabled() && window?.newrelic?.setCustomAttribute) {
    try {
      window.newrelic.setCustomAttribute(name, value);
    } catch {}
  }
};
